import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type ToastKind = 'info' | 'success' | 'warning' | 'error';
export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  toast: (kind: ToastKind, message: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);

const KIND: Record<ToastKind, string> = {
  info: 'border-info',
  success: 'border-success',
  warning: 'border-warning',
  error: 'border-danger',
};

/** Non-blocking notifications. Blocking confirmations use <Dialog> (11 §6 toast vs modal rule). */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const toast = useCallback((kind: ToastKind, message: string) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, kind, message }]);
    setTimeout(
      () => setItems((xs) => xs.filter((x) => x.id !== id)),
      kind === 'error' ? 8000 : 4000,
    );
  }, []);
  const api = useMemo(() => ({ toast }), [toast]);
  return (
    <Ctx.Provider value={api}>
      {children}
      <div
        // bottom-12: clear of the node-web status bar (h-9) so toasts never hide lock/logout
        className="pointer-events-none fixed bottom-12 end-4 z-50 flex w-[min(380px,90vw)] flex-col gap-2"
        aria-live="polite"
      >
        {items.map((t) => (
          <div
            key={t.id}
            role="status"
            className={`pointer-events-auto rounded-md border-s-4 bg-surface p-3 text-sm shadow ${KIND[t.kind]}`}
          >
            {t.message}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(Ctx);
  if (!api) throw new Error('useToast outside ToastProvider');
  return api;
}
